import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  Chat as MessagesIcon,
  School as SchoolIcon,
  MedicalServices as HealthIcon,
  SportsBasketball as ActivitiesIcon,
  Contacts as ContactsIcon,
  AccountBalance as MoneyIcon,
  EmojiEvents as DisciplineIcon,
  Note as TasksIcon,
  PhotoAlbum as MemoriesIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Keyboard as KeyboardIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../context/ThemeContext';
import { useHotkeys } from '../../context/HotkeysContext';
import { colors } from '../../theme';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  shortcut?: string;
  category: 'navigation' | 'action' | 'settings';
  action: () => void;
  color?: string;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useThemeMode();
  const { openHelpDialog } = useHotkeys();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const commands: CommandItem[] = useMemo(() => [
    // Navigation
    { id: 'home', label: 'Go to Home', icon: <HomeIcon />, shortcut: 'G H', category: 'navigation', action: () => navigate('/'), color: colors.primary },
    { id: 'calendar', label: 'Go to Calendar', icon: <CalendarIcon />, shortcut: 'G C', category: 'navigation', action: () => navigate('/calendar'), color: colors.calendar },
    { id: 'messages', label: 'Go to Messages', icon: <MessagesIcon />, shortcut: 'G M', category: 'navigation', action: () => navigate('/messages'), color: colors.messages },
    { id: 'school', label: 'Go to School', icon: <SchoolIcon />, shortcut: 'G S', category: 'navigation', action: () => navigate('/school'), color: colors.school },
    { id: 'health', label: 'Go to Health', icon: <HealthIcon />, shortcut: 'G E', category: 'navigation', action: () => navigate('/health'), color: colors.health },
    { id: 'activities', label: 'Go to Activities', icon: <ActivitiesIcon />, shortcut: 'G A', category: 'navigation', action: () => navigate('/activities'), color: colors.activities },
    { id: 'contacts', label: 'Go to Contacts', icon: <ContactsIcon />, shortcut: 'G O', category: 'navigation', action: () => navigate('/contacts'), color: colors.contacts },
    { id: 'money', label: 'Go to Money', icon: <MoneyIcon />, shortcut: 'G $', category: 'navigation', action: () => navigate('/money'), color: colors.money },
    { id: 'discipline', label: 'Go to Discipline', icon: <DisciplineIcon />, shortcut: 'G D', category: 'navigation', action: () => navigate('/discipline'), color: colors.discipline },
    { id: 'tasks', label: 'Go to Tasks', icon: <TasksIcon />, shortcut: 'G T', category: 'navigation', action: () => navigate('/tasks'), color: colors.notes },
    { id: 'memories', label: 'Go to Scrapbook', icon: <MemoriesIcon />, shortcut: 'G P', category: 'navigation', action: () => navigate('/memories'), color: colors.memories },
    { id: 'settings', label: 'Go to Settings', icon: <SettingsIcon />, shortcut: 'G ,', category: 'navigation', action: () => navigate('/settings'), color: colors.gray },

    // Actions (context-aware)
    { id: 'new-event', label: 'Create Event', description: 'Add a new calendar event', icon: <AddIcon />, category: 'action', action: () => navigate('/calendar?action=new'), color: colors.calendar },
    { id: 'new-contact', label: 'Add Contact', description: 'Add a new family contact', icon: <AddIcon />, category: 'action', action: () => navigate('/contacts?action=new'), color: colors.contacts },
    { id: 'new-task', label: 'Create Task', description: 'Add a new task or note', icon: <AddIcon />, category: 'action', action: () => navigate('/tasks?action=new'), color: colors.notes },

    // Settings
    { id: 'toggle-theme', label: mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode', icon: mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />, category: 'settings', action: () => toggleTheme() },
    { id: 'shortcuts', label: 'Keyboard Shortcuts', description: 'View all keyboard shortcuts', icon: <KeyboardIcon />, shortcut: '?', category: 'settings', action: () => openHelpDialog() },
  ], [navigate, mode, toggleTheme, openHelpDialog]);

  const filteredCommands = useMemo(() => {
    if (!search.trim()) return commands;
    const searchLower = search.toLowerCase();
    return commands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(searchLower) ||
        cmd.description?.toLowerCase().includes(searchLower) ||
        cmd.category.toLowerCase().includes(searchLower)
    );
  }, [commands, search]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Reset search and selection when dialog opens
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredCommands[selectedIndex]) {
          executeCommand(filteredCommands[selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
    }
  };

  const executeCommand = (command: CommandItem) => {
    onClose();
    // Small delay to allow dialog to close
    setTimeout(() => command.action(), 50);
  };

  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {
      navigation: [],
      action: [],
      settings: [],
    };
    filteredCommands.forEach((cmd) => {
      groups[cmd.category].push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'navigation': return 'Navigation';
      case 'action': return 'Actions';
      case 'settings': return 'Settings';
      default: return category;
    }
  };

  let currentIndex = -1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '15%',
          m: 0,
          maxHeight: '70vh',
          borderRadius: 2,
        },
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Type a command or search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Chip
                  label="ESC"
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
          }}
        />
      </Box>

      <Divider />

      <DialogContent sx={{ p: 1, maxHeight: 400, overflow: 'auto' }}>
        {filteredCommands.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No commands found</Typography>
          </Box>
        ) : (
          <List ref={listRef} dense disablePadding>
            {Object.entries(groupedCommands).map(([category, items]) => {
              if (items.length === 0) return null;
              return (
                <React.Fragment key={category}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 2, py: 1, display: 'block', fontWeight: 600 }}
                  >
                    {getCategoryLabel(category)}
                  </Typography>
                  {items.map((command) => {
                    currentIndex++;
                    const index = currentIndex;
                    const isSelected = index === selectedIndex;
                    return (
                      <ListItem key={command.id} disablePadding data-index={index}>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => executeCommand(command)}
                          sx={{
                            borderRadius: 1,
                            mx: 1,
                            '&.Mui-selected': {
                              bgcolor: 'action.selected',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 40,
                              color: command.color || 'inherit',
                            }}
                          >
                            {command.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={command.label}
                            secondary={command.description}
                            primaryTypographyProps={{ fontSize: '0.9rem' }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                          {command.shortcut && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {command.shortcut.split(' ').map((key, i) => (
                                <Chip
                                  key={i}
                                  label={key}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: 'action.hover',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      <Divider />
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          <Chip label="↑↓" size="small" sx={{ height: 18, mr: 0.5, fontSize: '0.6rem' }} />
          Navigate
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Chip label="↵" size="small" sx={{ height: 18, mr: 0.5, fontSize: '0.6rem' }} />
          Select
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Chip label="ESC" size="small" sx={{ height: 18, mr: 0.5, fontSize: '0.6rem' }} />
          Close
        </Typography>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
